/* eslint-disable react/no-string-refs */
import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { Widget, setMoreActions, toggleWidget } from 'tls-chat-widget';
import { useAuth } from './state/AuthProvider';
import { useTenant } from './state/TenantProvider';
import { sendChatMessageAsync } from './apis/session';
import { Helmet } from 'react-helmet-async';
import { useChat } from './state/ChatProvider';
import { MoreOptions } from './constants';

import GetStarted from './components/GetStarted';
import WelcomeBack from './components/WelcomeBack';
import Authorization from './components/Authorization';
import CloseConfirm from './components/CloseConfirm';
import Spinner from './components/Spinner';

import { ReactComponent as HumanIcon } from '../src/assets/human-chat.svg';
import { ReactComponent as ChatDismissIcon } from '../src/assets/chat-dismiss.svg';
import { ReactComponent as CheckedBox } from '../src/assets/checked-box.svg';
import { ReactComponent as UncheckedBox } from '../src/assets/unchecked-box.svg';
import { ReactComponent as LogoutIcon } from '../src/assets/Logout.svg';

import 'tls-chat-widget/lib/styles.css';
import useDebounce from './hooks/useDebounce';

const ChatWidget = () => {
  const { theme, logo, banner } = useTenant();
  const { token, isAuthenticated, requireAuthorization, setRequireAuthorization, isLoading, logOut } = useAuth();
  const [userMessagePreview, setUserMessagePreview] = useState('');
  const debouncedUserMessagePreview = useDebounce(userMessagePreview, 400);

  const {
    isChatOpen,
    setIsChatOpen,
    activeChatId,
    startChat,
    endChat,
    hasActiveChat,
    chatEndedMessage,
    setChatEndedMessage,
    requestAgent,
    showCloseConfirm,
    setShowCloseConfirm,
    botOrAgentName,
    widgetIsTyping,
    hasRequestedAgent,
    optOutTranscript,
    hasOptedOutTranscript
  } = useChat();

  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    const moreActions = [
      {
        label: MoreOptions.END_CONVERSATION.label,
        value: MoreOptions.END_CONVERSATION.value,
        icon: ChatDismissIcon
      },
      {
        label: MoreOptions.LOG_OUT.label,
        value: MoreOptions.LOG_OUT.value,
        icon: LogoutIcon
      },
      {
        label: MoreOptions.OPT_OUT_TRANSCRIPT.label,
        value: MoreOptions.OPT_OUT_TRANSCRIPT.value,
        icon: hasOptedOutTranscript ? CheckedBox : UncheckedBox
      }
    ];

    if(!hasRequestedAgent) {
      moreActions.push({
        label: MoreOptions.TALK_TO_A_HUMAN.label,
        value: MoreOptions.TALK_TO_A_HUMAN.value,
        icon: HumanIcon
      });
    }
    
    setMoreActions(moreActions);
  }, [hasRequestedAgent, optOutTranscript]);

  useLayoutEffect(() => {
    window.parent.postMessage({ isOpen: isChatOpen }, "*");
  }, [isChatOpen]);

  const handleToggle = useCallback((isOpen) => {
    //opening
    if (!isOpen) {
      setIsChatOpen(!isOpen);
      setTimeout(() => {
        toggleWidget();
      }, 10);
      return;
    }

    //closing
    if (activeChatId) {
      setShowCloseConfirm(true);
    } else {
      toggleWidget();
      setShowCloseConfirm(false);
      setChatEndedMessage("");
      setIsChatOpen(!isOpen);
    }
  }, [activeChatId]);

  const handleMinimise = () => {
    setIsChatOpen(false);
    toggleWidget();
  }

  const handleMessageSubmit = async (message) => {
    if (sendingMessage || !isAuthenticated || requireAuthorization) return;

    if (!activeChatId) {
      await startChat();
    }

    try {
      setUserMessagePreview('');
      setSendingMessage(true);
      const result = await sendChatMessageAsync(message);
      if (result.requireAuthorization) {
        setRequireAuthorization(true);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleActionMenuClicked = async (optionValue) => {
    if (!activeChatId) return;

    if (optionValue === MoreOptions.TALK_TO_A_HUMAN.value) {
      await requestAgent();
      return;
    }

    if (optionValue === MoreOptions.END_CONVERSATION.value) {
      await endChat();
    }

    if (optionValue === MoreOptions.OPT_OUT_TRANSCRIPT.value) {
      await optOutTranscript();
    }

    if (optionValue === MoreOptions.LOG_OUT.value) {
      await endChat();
      await logOut();
    }
  }

  const getLauncher = (handleToggle, isWidgetOpen) => {
    if (isWidgetOpen) return null;
    return (
      <button className="_kanis_launcher" type="button" onClick={handleToggle}>
      </button>
    )
  }

  const getOverlayComponent = useCallback(() => {
    
    if (isLoading) return <Spinner size={"large"} />

    const hasStartedSession = (token != null);

    if (!hasStartedSession) return <GetStarted />

    if(!hasActiveChat) return <WelcomeBack /> ;

    if (requireAuthorization) return <Authorization />

    if (showCloseConfirm) return <CloseConfirm />

    return null;

  }, [token, hasActiveChat, chatEndedMessage, isLoading, requireAuthorization, showCloseConfirm]);

  useEffect(() => {
    widgetIsTyping(debouncedUserMessagePreview);
  }, [debouncedUserMessagePreview]);

  return (
    <div className="_kanis_widget_root">
      <Helmet>
        {theme && <link rel={'stylesheet'} href={theme} />}
      </Helmet>
      <Widget
        title={`Chatting with ${botOrAgentName}`}
        logo={banner}
        senderPlaceHolder="Type in here"
        profileAvatar={logo}
        showTimeStamp={false}
        showBadge={false}
        handleSubmit={handleMessageSubmit}
        handleActionMenuClicked={handleActionMenuClicked}
        handleToggle={handleToggle}
        handleMinimise={handleMinimise}
        launcher={(handleToggle) => getLauncher(handleToggle, isChatOpen)}
        overlayComponent={getOverlayComponent()}
        handleTextInputChange={event => setUserMessagePreview(event?.currentTarget?.innerText)}
      />
    </div>
  );
}

export default ChatWidget;